import { useEffect, useState } from "react";
import api from "../lib/api";
import { LOSS_CATEGORIES } from "../lib/lossCategories";
import { toast } from "sonner";
import { PhoneCall, ShieldAlert, ShieldCheck } from "lucide-react";

const OUTCOMES = [
  ["connected_interested", "Connected — interested", "Engagement bump; optionally book a meeting below."],
  ["connected_not_interested", "Connected — not interested", "Routes the lead to Nurture (default) or Lost with a reason."],
  ["voicemail", "Voicemail left", "Logged, no negative signal. Retry later."],
  ["no_answer", "No answer", "Logged; supervisors notified for review."],
  ["callback_requested", "Callback requested", "Sets the lead's check-back date (Nurture) and notifies the owner."],
  ["wrong_number", "Wrong number", "Flags the contact's phone as invalid; the lead is not penalised."],
  ["do_not_call", "Do not call (DNC)", "Sets status DNC and suppresses all outreach. Logged for compliance."],
];
const inp = "border border-[#DFDAD0] px-2 py-1.5 text-sm bg-white outline-none focus:border-[#1C2530]";

export default function VoicePanel({ leadId, region, onChange }) {
  const [ext, setExt] = useState(null);
  const [reps, setReps] = useState([]);
  const [rep, setRep] = useState("");
  const [slot, setSlot] = useState("");
  const [outcome, setOutcome] = useState("connected_interested");
  const [callbackAt, setCallbackAt] = useState("");
  const [reason, setReason] = useState("");
  const [reasonCat, setReasonCat] = useState("Price / budget");
  const [route, setRoute] = useState("nurture");
  const [notes, setNotes] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setExt(r.data.external_calling_enabled)).catch(() => setExt(false));
    api.get("/users").then((r) => setReps(r.data.filter((u) => u.role === "rep")));
  }, []);

  const call = async () => {
    if (outcome === "callback_requested" && !callbackAt) { toast.error("Pick the callback date"); return; }
    if (outcome === "do_not_call" && !window.confirm("Mark this lead Do-Not-Call? All outreach will be suppressed.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/leads/${leadId}/voice-call`, {
        rep_id: outcome === "connected_interested" ? (rep || null) : null, slot: outcome === "connected_interested" ? (slot || null) : null,
        outcome, callback_at: callbackAt || null, reason: reason || null, not_interested_route: route, notes: notes || null,
        reason_category: outcome === "connected_not_interested" && route === "lost" ? reasonCat : null,
      });
      setRes(data);
      toast.success(`Call logged: ${data.outcome_label}${data.booked ? " · meeting booked" : ""}`);
      setNotes(""); setReason("");
      onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Call failed"); }
    finally { setBusy(false); }
  };
  const meta = OUTCOMES.find((o) => o[0] === outcome);

  return (
    <div className="border border-[#DFDAD0] bg-white p-5" data-testid="voice-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="font-mono text-[10px] uppercase tracking-wider text-[#1C2530]/60">Voice · log a call outcome</div>
        <span className="flex items-center gap-1 font-mono text-[9px] uppercase px-1.5 py-0.5 border" data-testid="ext-calling-badge"
          style={{ color: ext ? "#B23A2E" : "#2F7A3E", borderColor: ext ? "#B23A2E" : "#2F7A3E" }}>
          {ext ? <ShieldAlert className="w-3 h-3" /> : <ShieldCheck className="w-3 h-3" />} external calling {ext ? "ON" : "OFF"}
        </span>
      </div>
      <div className="font-mono text-[10px] text-[#1C2530]/45 mb-3">
        {ext ? "LIVE — real lead numbers will be dialed." : "TEST MODE — real numbers are refused at code level; log the outcome of your own call here."}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <select value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="voice-outcome" className={inp}>
          {OUTCOMES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {outcome === "callback_requested" && <input type="date" value={callbackAt} onChange={(e) => setCallbackAt(e.target.value)} data-testid="voice-callback-date" className={inp} />}
        {outcome === "connected_not_interested" && (
          <select value={route} onChange={(e) => setRoute(e.target.value)} data-testid="voice-route" className={inp}>
            <option value="nurture">Route → Nurture (check back in 90 days)</option>
            <option value="lost">Route → Lost</option>
          </select>
        )}
        {outcome === "connected_not_interested" && route === "lost" && (
          <select value={reasonCat} onChange={(e) => setReasonCat(e.target.value)} data-testid="voice-reason-category" className={inp}>
            {LOSS_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        {["connected_not_interested", "do_not_call"].includes(outcome) && (
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" data-testid="voice-reason" className={inp} />
        )}
        {outcome === "connected_interested" && (<>
          <select value={rep} onChange={(e) => setRep(e.target.value)} data-testid="voice-rep" className={inp}>
            <option value="">Book rep (optional)…</option>
            {reps.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.region})</option>)}
          </select>
          <input type="datetime-local" value={slot} onChange={(e) => setSlot(e.target.value)} data-testid="voice-slot" className={inp} />
        </>)}
        <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Call notes (optional)" data-testid="voice-notes" className={`${inp} md:col-span-2`} />
        <button onClick={call} disabled={busy} data-testid="voice-call-btn" className="flex items-center justify-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644] disabled:opacity-40">
          <PhoneCall className="w-3.5 h-3.5" /> {busy ? "Logging…" : "Log call"}
        </button>
      </div>
      {meta && <div className="mt-2 text-[11px] text-[#1C2530]/55" data-testid="voice-outcome-hint">{meta[2]}</div>}
      {res && (
        <div className="mt-3 border border-[#DFDAD0] bg-[#F5F3EE] p-3" data-testid="voice-result">
          <div className="font-mono text-[10px] text-[#1C2530]/50 mb-1">MODE {res.mode} · dialed {res.dialed} · {res.outcome_label}{res.booked ? ` · booked ${new Date(res.booked).toLocaleString()}` : ""}{res.callback_at ? ` · callback ${res.callback_at}` : ""}</div>
          {res.transcript && <div className="text-xs text-[#1C2530]/80 whitespace-pre-wrap">{res.transcript}</div>}
        </div>
      )}
    </div>
  );
}
